import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Row, Col, Panel, Button } from 'react-bootstrap';
import { exportAsExcel } from '../../Services/exportAsExcel';
import { fetchApi } from '../../Network/NetworkService';
import EnhancedChart from '../Ui/EnhancedChart';
import SimpleChart from '../Ui/SimpleChart';

const RecurrenceMedecins = () => {
    const [doctorData, setDoctorData] = useState([]);
    const [selectedDoctors, setSelectedDoctors] = useState([]);
    const [comparisonData, setComparisonData] = useState([]);
    const [detailData, setDetailData] = useState(null);
    const [selectedDoctorId, setSelectedDoctorId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        start: '2023-01-01',
        end: new Date().toISOString().split('T')[0],
        minRequests: 10
    });
    const [sortConfig, setSortConfig] = useState({
        key: 'retentionRate',
        direction: 'desc'
    });

    // Fetch main doctor recurrence data
    const fetchDoctorRecurrence = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchApi(
                `/doctor-recurrence/analytics/doctor-recurrence?start=${filters.start}&end=${filters.end}&minRequests=${filters.minRequests}`,
                'GET'
            );
            if (response.success) {
                setDoctorData(response.data);
            } else {
                setError('Failed to fetch doctor recurrence data');
            }
        } catch (err) {
            setError(`Error fetching data: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // Fetch detailed data for a specific doctor
    const fetchDoctorDetail = async (doctorId) => {
        console.log('Fetching details for doctor:', doctorId);
        setLoading(true);
        setError(null);
        try {
            const response = await fetchApi(
                `/doctor-recurrence/analytics/doctor-recurrence/${doctorId}?start=${filters.start}&end=${filters.end}`,
                'GET'
            );
            console.log('Detail response:', response);
            if (response.success && response.data) {
                console.log('Setting detail data with cohortData:', response.data.cohortData);
                setDetailData(response.data);
                setSelectedDoctorId(doctorId);
            } else {
                setError(`Failed to fetch doctor details: ${response.message || 'Unknown error'}`);
            }
        } catch (err) {
            console.error('Error fetching doctor details:', err);
            setError(`Error fetching doctor details: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Compare selected doctors
    const compareDoctors = async () => {
        if (selectedDoctors.length < 2) {
            setError('Please select at least 2 doctors to compare');
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetchApi(
                '/doctor-recurrence/analytics/doctor-recurrence/compare',
                'POST',
                {
                    doctorIds: selectedDoctors,
                    start: filters.start,
                    end: filters.end
                }
            );
            if (response.success) {
                setComparisonData(response.data);
            }
        } catch (err) {
            setError(`Error comparing doctors: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchDoctorRecurrence();
    }, [fetchDoctorRecurrence]);

    // Handle sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Sort data
    const sortedData = React.useMemo(() => {
        let sortableData = [...doctorData];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                if (a[sortConfig.key] === null) return 1;
                if (b[sortConfig.key] === null) return -1;
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [doctorData, sortConfig]);

    // Export to Excel
    const handleExport = () => {
        const exportData = sortedData.map(doctor => ({
            'Médecin': doctor.doctorName,
            'Patients uniques': doctor.uniquePatients,
            'Total consultations': doctor.totalRequests,
            'Patients récurrents': doctor.returningPatients,
            'Récurrents actifs': doctor.retainedPatients || 0,
            'Récurrents perdus': doctor.lostPatients || 0,
            'Taux de rétention (%)': doctor.retentionRate,
            'Moy. consultations/patient': doctor.avgConsultationsPerPatient
        }));
        
        exportAsExcel(exportData, 'recurrence_medecins');
    };

    // Prepare chart data for retention rates
    const chartData = React.useMemo(() => {
        if (!doctorData.length) return null;
        
        const topDoctors = sortedData.slice(0, 10);
        return {
            categories: topDoctors.map(d => d.doctorName),
            series: [{
                name: 'Taux de rétention (%)',
                data: topDoctors.map(d => d.retentionRate)
            }]
        };
    }, [sortedData]);

    // Prepare comparison chart data
    const comparisonChartData = React.useMemo(() => {
        if (!comparisonData.length) return null;
        
        const periods = [...new Set(comparisonData.map(d => d.period))].sort();
        const doctors = [...new Set(comparisonData.map(d => d.doctorName))];
        
        return {
            categories: periods,
            series: doctors.map(doctorName => ({
                name: doctorName,
                data: periods.map(period => {
                    const item = comparisonData.find(
                        d => d.doctorName === doctorName && d.period === period
                    );
                    return item ? item.retentionRate : 0;
                })
            }))
        };
    }, [comparisonData]);

    const getRetentionColor = (rate) => {
        if (rate >= 40) return '#5cb85c';
        if (rate >= 25) return '#f0ad4e';
        return '#d9534f';
    };

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>Récurrence Médecins</h2>
                    <p className="text-muted">
                        Analyse de la récurrence des patients selon les médecins traitants
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger alert-dismissible" role="alert">
                            <button type="button" className="close" onClick={() => setError(null)}>
                                <span>&times;</span>
                            </button>
                            {error}
                        </div>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col md={3}>
                    <div className="form-group">
                        <label>Date début</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.start}
                            onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>Date fin</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.end}
                            onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>Min. consultations</label>
                        <input
                            type="number"
                            className="form-control"
                            value={filters.minRequests}
                            onChange={(e) => setFilters({ ...filters, minRequests: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group" style={{ paddingTop: '25px' }}>
                        <Button 
                            bsStyle="primary" 
                            onClick={fetchDoctorRecurrence}
                            disabled={loading}
                            style={{ marginRight: '10px' }}
                        >
                            {loading ? 'Chargement...' : 'Actualiser'}
                        </Button>
                        <Button bsStyle="success" onClick={handleExport} disabled={!doctorData.length}>
                            Exporter Excel
                        </Button>
                    </div>
                </Col>
            </Row>

            {chartData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Top 10 Médecins - Taux de Rétention</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    height={300}
                                    categories={chartData.categories}
                                    series={chartData.series}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col xs={12}>
                    <Panel>
                        <Panel.Heading>
                            <h4>Tableau de Récurrence par Médecin</h4>
                        </Panel.Heading>
                        <Panel.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            <table className="table table-striped table-bordered table-hover">
                                <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedDoctors(doctorData.map(d => d.doctorId));
                                                    } else {
                                                        setSelectedDoctors([]);
                                                    }
                                                }}
                                            />
                                        </th>
                                        <th 
                                            onClick={() => handleSort('doctorName')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Médecin {sortConfig.key === 'doctorName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('uniquePatients')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Patients uniques {sortConfig.key === 'uniquePatients' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('totalRequests')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Total consultations {sortConfig.key === 'totalRequests' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('returningPatients')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Patients récurrents {sortConfig.key === 'returningPatients' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('retainedPatients')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Récurrents actifs {sortConfig.key === 'retainedPatients' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('lostPatients')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Récurrents perdus {sortConfig.key === 'lostPatients' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('retentionRate')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Taux rétention (%) {sortConfig.key === 'retentionRate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th 
                                            onClick={() => handleSort('avgConsultationsPerPatient')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Moy. consult./patient {sortConfig.key === 'avgConsultationsPerPatient' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedData.map((doctor) => (
                                        <tr key={doctor.doctorId}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDoctors.includes(doctor.doctorId)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedDoctors([...selectedDoctors, doctor.doctorId]);
                                                        } else {
                                                            setSelectedDoctors(selectedDoctors.filter(id => id !== doctor.doctorId));
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td>{doctor.doctorName}</td>
                                            <td>{doctor.uniquePatients}</td>
                                            <td>{doctor.totalRequests}</td>
                                            <td>{doctor.returningPatients}</td>
                                            <td>
                                                <span className="label label-success">
                                                    {doctor.retainedPatients || 0}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="label label-danger">
                                                    {doctor.lostPatients || 0}
                                                </span>
                                            </td>
                                            <td>
                                                <span 
                                                    className="label"
                                                    style={{ 
                                                        backgroundColor: getRetentionColor(doctor.retentionRate),
                                                        color: 'white',
                                                        padding: '3px 8px'
                                                    }}
                                                >
                                                    {doctor.retentionRate}%
                                                </span>
                                            </td>
                                            <td>{doctor.avgConsultationsPerPatient}</td>
                                            <td>
                                                <Button
                                                    bsSize="small"
                                                    bsStyle="info"
                                                    onClick={() => fetchDoctorDetail(doctor.doctorId)}
                                                >
                                                    Détails
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Panel.Body>
                        {selectedDoctors.length >= 2 && (
                            <Panel.Footer>
                                <Button 
                                    bsStyle="primary" 
                                    onClick={compareDoctors}
                                    disabled={loading}
                                >
                                    Comparer {selectedDoctors.length} médecins sélectionnés
                                </Button>
                            </Panel.Footer>
                        )}
                    </Panel>
                </Col>
            </Row>

            {comparisonChartData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Comparaison des Médecins</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <EnhancedChart
                                    type="line"
                                    height={400}
                                    categories={comparisonChartData.categories}
                                    series={comparisonChartData.series}
                                    options={{
                                        yaxis: {
                                            title: { text: 'Taux de rétention (%)' }
                                        }
                                    }}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {detailData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Détails - Médecin</h4>
                                <Button 
                                    bsSize="small" 
                                    bsStyle="default" 
                                    className="pull-right"
                                    onClick={() => {
                                        setDetailData(null);
                                        setSelectedDoctorId(null);
                                    }}
                                >
                                    Fermer
                                </Button>
                            </Panel.Heading>
                            <Panel.Body>
                                <Row>
                                    <Col md={6}>
                                        <h5>Évolution mensuelle</h5>
                                        {detailData.cohortData && detailData.cohortData.length > 0 ? (
                                            <SimpleChart
                                                type="area"
                                                height={250}
                                                categories={detailData.cohortData.map(d => d.period)}
                                                series={[
                                                    {
                                                        name: 'Nouveaux patients',
                                                        data: detailData.cohortData.map(d => d.newPatients)
                                                    },
                                                    {
                                                        name: 'Total consultations',
                                                        data: detailData.cohortData.map(d => d.totalRequests)
                                                    }
                                                ]}
                                            />
                                        ) : (
                                            <p className="text-muted">Aucune donnée disponible</p>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        <h5>Temps entre consultations</h5>
                                        <table className="table table-striped">
                                            <tbody>
                                                <tr>
                                                    <td>Moyenne</td>
                                                    <td>
                                                        {detailData.timeBetweenConsultations.avgDaysBetweenConsultations 
                                                            ? `${Math.round(detailData.timeBetweenConsultations.avgDaysBetweenConsultations)} jours`
                                                            : 'N/A'
                                                        }
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Minimum</td>
                                                    <td>
                                                        {detailData.timeBetweenConsultations.minDays 
                                                            ? `${Math.round(detailData.timeBetweenConsultations.minDays)} jours`
                                                            : 'N/A'
                                                        }
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Maximum</td>
                                                    <td>
                                                        {detailData.timeBetweenConsultations.maxDays 
                                                            ? `${Math.round(detailData.timeBetweenConsultations.maxDays)} jours`
                                                            : 'N/A'
                                                        }
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Col>
                                </Row>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}
        </Grid>
    );
};

export default RecurrenceMedecins;